import { Injectable } from '@angular/core';
import { Nullable } from 'src/core/common/domain/types/types';
import { FilesystemService } from 'src/core/filesystem/domain/filesystem.service';
import { FileDownloadOptions, FileDownloadTask } from 'src/core/file-download/domain/file-download.types';
import { FileDownloadService } from 'src/core/file-download/domain/file-download.service';
import { LocalFileStore } from 'src/core/file-download/domain/local-file-store';
import { DescriptionFilePrimitives } from 'src/core/master-data/domain/task';

@Injectable({
  providedIn: 'root',
})
export class DescriptionFileCacheService {
  constructor(
    private readonly filesystemService: FilesystemService,
    private readonly fileDownloadService: FileDownloadService,
    private readonly localFileStore: LocalFileStore
  ) {}

  buildCacheKey(descriptionFile: DescriptionFilePrimitives): string {
    const fileId = descriptionFile.id;
    return `df_${this.fnv1aHash(fileId)}`;
  }

  buildStorageFilename(descriptionFile: DescriptionFilePrimitives): string {
    const extension = this.resolveExtension(descriptionFile);
    const cacheKey = this.buildCacheKey(descriptionFile);
    const filename = extension ? `${cacheKey}.${extension}` : cacheKey;

    return this.filesystemService.sanitizeFilename(filename) || `${cacheKey}.jpg`;
  }

  async resolveExistingLocalUrl(
    descriptionFile: DescriptionFilePrimitives,
    destinationFolder: string
  ): Promise<Nullable<string>> {
    const filename = this.buildStorageFilename(descriptionFile);
    return await this.localFileStore.resolveLocalUrl(destinationFolder, filename);
  }

  async hasLocalCopy(descriptionFile: DescriptionFilePrimitives, destinationFolder: string): Promise<boolean> {
    const filename = this.buildStorageFilename(descriptionFile);
    return await this.localFileStore.fileExists(destinationFolder, filename);
  }

  releaseLocalUrl(descriptionFile: DescriptionFilePrimitives, destinationFolder: string): void {
    const filename = this.buildStorageFilename(descriptionFile);
    this.localFileStore.releaseLocalUrl(destinationFolder, filename);
  }

  downloadToCache(
    descriptionFile: DescriptionFilePrimitives,
    destinationFolder: string,
    options?: Omit<FileDownloadOptions, 'filename' | 'preferUniqueFilename'>
  ): FileDownloadTask {
    const filename = this.buildStorageFilename(descriptionFile);

    return this.fileDownloadService.downloadToAttachment(descriptionFile.url, destinationFolder, {
      ...options,
      filename,
      preferUniqueFilename: false,
    });
  }

  async clearAllCachedDescriptionFiles(): Promise<number> {
    return await this.localFileStore.clearCachedFiles();
  }

  private resolveExtension(descriptionFile: DescriptionFilePrimitives): string {
    const filename = descriptionFile.filename?.trim() || this.filesystemService.getFilenameFromUrl(descriptionFile.url);
    const extension = this.filesystemService.getExtension(filename).toLowerCase();

    if (extension) {
      return extension;
    }

    return 'jpg';
  }

  private fnv1aHash(source: string): string {
    let hash = 0x811c9dc5;

    for (let index = 0; index < source.length; index += 1) {
      hash ^= source.charCodeAt(index);
      hash = (hash * 0x01000193) >>> 0;
    }

    return hash.toString(16).padStart(8, '0');
  }
}
